/**
 * pdp-dimensions
 *
 * Imperial / metric toggle for the Dimensions accordion. Both unit
 * systems are rendered server-side, so this only flips which set is visible.
 *
 * The accordion stack is cloned into the Mobile PDP Accordions section, and
 * replaced outright on variant change (data-force-update), which means any
 * given element is short-lived. The selection is therefore held in module scope
 * and re-applied from connectedCallback, and a change on one instance is
 * mirrored onto every other so the two stacks never disagree.
 */

// null until the shopper picks a unit — before that each instance falls back to
// the market default rendered onto the element.
let selectedUnit = null;

class PdpDimensions extends HTMLElement {
  connectedCallback() {
    this.buttons = [...this.querySelectorAll("[data-unit]")];

    this.handleClick = (e) => {
      // The zoom modal carries its own `pdp-dimensions`, which picks the unit
      // up from the shared selection — nothing about it travels on the event.
      const zoom = e.target.closest("[data-dimensions-zoom]");

      if (zoom) {
        zoom.dispatchEvent(
          new CustomEvent("zoom-gallery:open", {
            bubbles: true,
            detail: { tab: "dimensions" },
          }),
        );
        return;
      }

      const button = e.target.closest("[data-unit]");

      if (!button || !this.contains(button)) return;

      selectedUnit = button.dataset.unit;

      document
        .querySelectorAll("pdp-dimensions")
        .forEach((element) => element.render?.());
    };

    this.addEventListener("click", this.handleClick);

    this.render();
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.handleClick);
  }

  render() {
    if (!this.buttons) return;

    const unit = selectedUnit || this.defaultUnit;

    this.setAttribute("unit", unit);

    this.buttons.forEach((button) => {
      button.setAttribute("aria-pressed", String(button.dataset.unit === unit));
    });
  }

  get defaultUnit() {
    return this.getAttribute("default-unit") === "cm" ? "cm" : "in";
  }
}

if (!customElements.get("pdp-dimensions")) {
  customElements.define("pdp-dimensions", PdpDimensions);
}
